import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  /** Feature 034: null = onboarding not completed, ISO string = completed */
  onboardingCompletedAt: string | null;
}

/** Shape returned by verify_magic_link / refresh_tokens / exchange_oauth_code mutations */
export interface AuthPayload {
  accessToken: string;
  refreshToken: string;
  tokenType: string; // always "Bearer"
  expiresIn: number; // seconds
  user: AuthUser;
}

export interface AuthState {
  /** JWT access token (15-minute expiry) */
  accessToken: string | null;

  /** JWT refresh token (7-day expiry) */
  refreshToken: string | null;

  /** Authenticated user profile from auth_payload */
  user: AuthUser | null;

  /** Computed: true if accessToken is non-null */
  isAuthenticated: boolean;

  /** Whether a token refresh is currently in progress (prevents concurrent refreshes) */
  isRefreshing: boolean;

  /** Store tokens and user after successful authentication */
  signIn: (payload: AuthPayload) => void;

  /** Clear all tokens and user, reset to unauthenticated state */
  signOut: () => void;

  /** Update tokens after silent refresh (preserves user) */
  updateTokens: (accessToken: string, refreshToken: string) => void;

  /** Set refreshing flag to prevent concurrent refresh attempts */
  setRefreshing: (isRefreshing: boolean) => void;
}

const initialState = {
  accessToken: null,
  refreshToken: null,
  user: null,
  isAuthenticated: false,
  isRefreshing: false,
} as const;

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      ...initialState,

      signIn: (payload: AuthPayload) => {
        // Validate tokens are non-empty strings
        if (!payload.accessToken || !payload.refreshToken) {
          return;
        }

        // Clear stale onboarding state from any previous session so the
        // backend-derived step is authoritative for the new session.
        // This prevents localStorage from showing a completed step when
        // the backend has reset onboarding (re-onboarding scenario).
        try {
          localStorage.removeItem("onboarding-storage");
        } catch {
          // Ignore storage errors
        }

        set({
          accessToken: payload.accessToken,
          refreshToken: payload.refreshToken,
          user: payload.user,
          isAuthenticated: true,
          isRefreshing: false,
        });
      },

      signOut: () => {
        set({
          ...initialState,
        });
      },

      updateTokens: (accessToken: string, refreshToken: string) => {
        // Validate tokens are non-empty strings
        if (!accessToken || !refreshToken) {
          return;
        }
        set({
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isRefreshing: false,
        });
      },

      setRefreshing: (isRefreshing: boolean) => {
        set({ isRefreshing });
      },
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => localStorage),
      // Only persist token and user data, not ephemeral flags
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => {
        return (state, error) => {
          if (error) {
            // Corrupt localStorage — clear auth data, don't crash
            console.warn("Failed to rehydrate auth store, clearing corrupt data:", error);
            try {
              localStorage.removeItem("auth-storage");
            } catch {
              // Ignore storage errors
            }
            return;
          }

          // Validate rehydrated data integrity
          if (state) {
            const hasValidTokens =
              typeof state.accessToken === "string" &&
              state.accessToken.length > 0 &&
              typeof state.refreshToken === "string" &&
              state.refreshToken.length > 0;

            if (!hasValidTokens) {
              // Corrupt or incomplete token data — reset to clean state
              state.signOut();
            } else {
              // Ensure isAuthenticated is derived correctly
              state.isAuthenticated = true;
            }

            // Always reset ephemeral flags
            state.isRefreshing = false;
          }
        };
      },
    },
  ),
);
